import AsyncStorage from "@react-native-async-storage/async-storage";
import React, { useCallback, useEffect, useMemo } from "react";

import isAccessTokenExpired from "@/app/(root)/utils/isAccessTokenExpired";
import {
  AuthAuthenticationResultFragment,
  ConfirmSignInResult as AuthConfirmSignInResult,
  SignInResult,
  ValidationCodes,
} from "@/generated/graphql";
import { env } from "@/lib/env";

import { getExpFromToken, performRefreshOnBoot } from "./refreshOnBoot";

/**
 * Props for AuthProvider component
 * @property root0.children - Child components that can access authentication context
 */
interface Props {
  children: React.ReactNode;
}

/**
 * Result of sign-in confirmation flow
 * @property success - Whether sign-in confirmation was successful
 * @property session - Session token if confirmation successful
 */
export interface ConfirmSignInResult {
  success: boolean;
  session: string | null | undefined;
}

/**
 * Extended authentication result with session and expiration data
 * @property session - Bearer token session for API authentication
 * @property identifier - User identifier (phone number or email)
 * @property exp - Token expiration timestamp in seconds since Unix epoch
 */
interface AuthAuthenticationResultFragmentWithSession extends Omit<
  AuthAuthenticationResultFragment,
  "session"
> {
  session?: string | null;
  identifier?: string;
  exp?: number;
}

/**
 * Result of confirming sign-in with success status and message
 */
export interface ConfirmSignInResultWithMessage {
  success: boolean;
  session: string | null;
  message: string | null;
}

/**
 * Build the failure result when confirmSignIn returns a signInResult (new challenge).
 * Extracts Session from the sign-in response and packages it as a failure result.
 * @param signInResult - The sign-in result containing a new session challenge
 * @param signInResult.data - Data object from the sign-in response
 * @param signInResult.data.Session - Session token for the new challenge, or null
 * @param signInResult.message - Human-readable message describing the challenge
 * @returns ConfirmSignInResultWithMessage indicating failure with session data
 */
export function buildSignInResultResponse(signInResult: {
  data: { Session?: string | null };
  message?: string | null;
}): ConfirmSignInResultWithMessage {
  const newSession = signInResult.data.Session ?? null;
  return {
    success: false,
    session: newSession,
    message: signInResult.message ?? null,
  };
}

/**
 * Build the error result when confirmSignIn has neither authResult nor signInResult.
 * Falls back to a default error message when errorMessage is null/undefined.
 * @param errorMessage - The error message object from the confirm sign-in response
 * @returns ConfirmSignInResultWithMessage indicating failure with error message
 */
export function buildErrorResponse(
  errorMessage: { message?: string | null } | null | undefined
): ConfirmSignInResultWithMessage {
  return {
    success: false,
    session: null,
    message: errorMessage?.message ?? "Something went wrong.",
  };
}

/**
 * Normalize validation codes from confirmSignIn response, treating null/undefined as empty.
 * @param validationCodes - Raw validation codes from the GraphQL response
 * @returns Readonly array of validation codes, empty if input was null/undefined
 */
export function normalizeValidationCodes(
  validationCodes: readonly ValidationCodes[] | null | undefined
): readonly ValidationCodes[] {
  return validationCodes ?? [];
}

/**
 * Authentication context value providing auth operations and state
 * @property onCompleteConfirmSignIn - Callback to finalize sign-in confirmation with OTP code
 * @property onCompleteSignOut - Callback to complete user sign-out and clear session
 * @property onCompleteSignIn - Callback to complete initial sign-in with identifier
 * @property onRefreshToken - Callback to update access token on refresh
 * @property onRefreshTokenError - Callback invoked when token refresh fails
 * @property onCompleteRegistration - Callback invoked when registration is complete
 * @property data - Current authentication data including tokens and user info
 * @property isLoggedIn - Whether user has active authentication session
 * @property isLoaded - Whether auth state has finished loading from AsyncStorage
 * @property validationCodes - Validation codes from confirmSignIn indicating missing profile fields
 */
interface Value {
  onCompleteConfirmSignIn: (
    result: AuthConfirmSignInResult
  ) => Promise<ConfirmSignInResultWithMessage>;
  onCompleteSignOut: () => Promise<void>;
  onCompleteSignIn: (result: SignInResult, identifier: string) => Promise<void>;
  onRefreshToken: (token: string) => Promise<void>;
  onRefreshTokenError: (error: Error) => Promise<void>;
  onCompleteRegistration: () => void;
  data: AuthAuthenticationResultFragmentWithSession | null | undefined;
  isLoggedIn: boolean;
  isLoaded: boolean;
  validationCodes: readonly ValidationCodes[];
}

const AuthContext = React.createContext<Value | null>(null);

/**
 * Calculate absolute token expiration timestamp from relative duration
 * @param expiresIn - Token lifetime in seconds from now
 * @returns Unix timestamp (seconds) when token will expire
 */
export function calculateTokenExpiry(expiresIn: number): number {
  // Get the current time in seconds since the Unix epoch
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);

  // Calculate the token's expiration time
  return currentTimeInSeconds + expiresIn;
}

/**
 * Calculate auth data with proper expiration timestamp handling
 *
 * Preserves existing exp if present, otherwise calculates from expiresIn.
 * Returns null for null, undefined, or empty auth objects.
 * @param auth - Authentication result data with optional exp and expiresIn fields
 * @returns Auth data with exp field properly set, or null if auth is empty/invalid
 */
export function calculateAuthWithExpiry(
  auth: AuthAuthenticationResultFragmentWithSession | null | undefined
): AuthAuthenticationResultFragmentWithSession | null {
  if (!auth || !Object.keys(auth).length) {
    return null;
  }

  return {
    ...auth,
    exp:
      auth.exp ??
      (auth.expiresIn ? calculateTokenExpiry(auth.expiresIn) : undefined),
  };
}

// This hook can be used to access the auth info.
export const useAuth = () => React.useContext(AuthContext);

/**
 * Authentication provider component managing auth state and operations
 *
 * Provides authentication context to child components including sign-in/sign-out operations,
 * token management, and session persistence with AsyncStorage.
 * @param root0 - Component props
 * @param root0.children - Child components that can access authentication context
 */
export /**
 *
 */
const AuthProvider = ({ children }: Props) => {
  const [data, setData] = React.useState<
    AuthAuthenticationResultFragmentWithSession | null | undefined
  >(undefined);
  const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);
  const [validationCodes, setValidationCodes] = React.useState<
    readonly ValidationCodes[]
  >([]);

  const initializeAuthData = useCallback(
    (auth: AuthAuthenticationResultFragmentWithSession | null | undefined) => {
      const authWithExpiry = calculateAuthWithExpiry(auth);

      if (!auth && isLoggedIn) {
        setIsLoggedIn(false);
      }

      setData(authWithExpiry);

      if (authWithExpiry) {
        setIsLoggedIn(true);
        return AsyncStorage.setItem(
          "@authData",
          JSON.stringify({
            ...authWithExpiry,
          })
        );
      }
      return AsyncStorage.removeItem("@authData");
    },
    [isLoggedIn]
  );

  const handleCompleteSignIn = useCallback(
    async ({ data }: SignInResult, identifier: string) => {
      if (data.Session) {
        await initializeAuthData({ session: data.Session, identifier });
      }
      // Always return a resolved promise to continue the promise chain
      // This is crucial when there's an OTP challenge (no Session yet)
      return Promise.resolve();
    },

    [initializeAuthData]
  );

  const handleCompleteConfirmSignIn = useCallback(
    async (result: AuthConfirmSignInResult) => {
      setValidationCodes(normalizeValidationCodes(result.validationCodes));

      if (result.authResult) {
        try {
          await initializeAuthData({ ...result.authResult.data });
          setIsLoggedIn(true);
          return {
            success: true,
            session: null,
            message: null,
          };
        } catch (error) {
          await initializeAuthData({ ...result.authResult.data });
          return {
            success: true,
            session: null,
            message: error instanceof Error ? error.message : "Unknown error",
          };
        }
      } else if (result.signInResult) {
        const response = buildSignInResultResponse(result.signInResult);
        await initializeAuthData({ ...data, session: response.session });
        return response;
      } else {
        return buildErrorResponse(result.errorMessage);
      }
    },

    [data, initializeAuthData]
  );

  const handleCompleteSignOut = useCallback(() => {
    setValidationCodes([]);
    return initializeAuthData(null);
  }, [initializeAuthData]);

  const handleCompleteRegistration = useCallback(() => {
    setValidationCodes([]);
  }, []);

  const handleRefreshToken = useCallback(
    (token: string) => {
      // SE-5105: also propagate the new JWT's exp. The pre-SE-5105 code
      // only spread `data` + new `accessToken`, leaving the OLD exp in
      // place — which made isAccessTokenExpired return true on the very
      // next boot even though the token was actually fresh, kicking the
      // user back to /login.
      return initializeAuthData({
        ...data,
        accessToken: token,
        exp: getExpFromToken(token),
      });
    },
    [data, initializeAuthData]
  );

  const handleRefreshTokenError = useCallback(
    (error: Error) => {
      console.log("Error refreshing token", error);
      return initializeAuthData(null);
    },
    [initializeAuthData]
  );

  const isLoaded = data !== undefined;

  const value: Value = useMemo(
    () => ({
      onCompleteSignIn: handleCompleteSignIn,
      onCompleteConfirmSignIn: handleCompleteConfirmSignIn,
      onCompleteSignOut: handleCompleteSignOut,
      onRefreshToken: handleRefreshToken,
      onRefreshTokenError: handleRefreshTokenError,
      onCompleteRegistration: handleCompleteRegistration,
      data,
      isLoggedIn,
      isLoaded,
      validationCodes,
    }),
    [
      handleCompleteSignIn,
      handleCompleteConfirmSignIn,
      handleCompleteSignOut,
      handleRefreshToken,
      handleRefreshTokenError,
      data,
      isLoggedIn,
      isLoaded,
      validationCodes,
      handleCompleteRegistration,
    ]
  );

  useEffect(() => {
    if (isLoaded) {
      return;
    }
    AsyncStorage.getItem("@authData").then(async dataString => {
      const auth: AuthAuthenticationResultFragmentWithSession | null =
        dataString ? JSON.parse(dataString) : null;
      if (isLoggedIn && !dataString) {
        setIsLoggedIn(false);
      }

      // SE-5105 — root cause for the constant-reload bug. SE-5107 shipped
      // the symptom fix: sign the user out when @authData has an expired
      // access token at boot. Here we go one better: if the access token
      // is stale but the refresh token is still valid, mint a fresh one
      // via raw fetch (NOT through Apollo — that would re-introduce the
      // /knowledge-graph reload regression by coupling route mount to
      // auth refresh, and Apollo isn't built yet at this point in the
      // tree anyway). On any refresh failure (revoked / network /
      // timeout) we initialize as null and let the existing SE-5107
      // safety net redirect to /login.
      //
      // We intentionally do NOT setData with the stale auth first — the
      // existing isAwaitingAuthResults gating (data === undefined) keeps
      // the boot UI from flashing the wrong screen while the refresh
      // resolves.
      if (auth && isAccessTokenExpired(auth.exp) && auth.refreshToken) {
        const refreshed = await performRefreshOnBoot({
          refreshToken: auth.refreshToken,
          graphqlUrl: env.EXPO_PUBLIC_GRAPHQL_BASE_URL,
        });
        if (refreshed) {
          return initializeAuthData({
            ...auth,
            accessToken: refreshed.accessToken,
            exp: refreshed.exp,
          });
        }
        return initializeAuthData(null);
      }

      return initializeAuthData(auth);
    });
  }, [isLoaded, initializeAuthData, isLoggedIn]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
